import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromGenerated from '../../../_generated';
import * as fromShared from '../../shared';
import {
  chatsFeature,
  ChatsState,
  SelectDocumentInChatMessagePayload,
} from '../../store';
import { DocumentInChatMessage } from '../conversation.page';
import {
  ImageInMessageClickedEvent,
  MessageDocuments,
  MessagesListingMessageComponent,
} from './message/message-listing-message.component';

export interface MessageListingType {
  id: number;
  content: string;
  documents?: (MessageDocuments &
    Partial<Pick<fromGenerated.DocumentView, 'role'>>)[];
  isFromUser: boolean;
  documentsChunks?: fromGenerated.DocumentChunkView[];
}

/**
 * Drops instruction-role documents (the skill.md, SB-3325/KD-15) so they never
 * render as chips on a chat message. The assistant profile and chat side-panel
 * already filter these out via `toSources` in `chats.selectors.ts`; the answer
 * message binds `message.documents` directly, so it needs the same filter here —
 * before the child `[messageDocuments]` binding, since `MessageDocuments` carries
 * no `role`. Mirrors `isInstructionFile`'s intent for message docs, where
 * `role === 'instruction'` alone is sufficient (the filename fallback there is
 * scoped to assistant-owned docs, so genuine user uploads named skill.md — which
 * keep role `regular` — still render).
 */
export const excludeInstructionDocuments = <
  T extends Partial<Pick<fromGenerated.DocumentView, 'role'>>,
>(
  documents: readonly T[] | undefined,
): T[] =>
  (documents ?? []).filter((document) => document.role !== 'instruction');

@Component({
  selector: 'squadbox-messages-listing',
  imports: [MessagesListingMessageComponent],
  template: `
    <div
      class="flex flex-col gap-10"
      [class.pb-4]="!isInCompliancePhaseRegular()"
    >
      <!-- LOADING MESSAGES -->
      @if (messageCount(); as messageCount) {
        @if (isLoading()) {
          @for (
            message of [].constructor(messageCount);
            track $index;
            let i = $index
          ) {
            <squadbox-messages-listing-message
              [isFromUser]="i % 2 !== 0"
              [isLoading]="true"
              [messageId]="i"
              [messageContent]="loadingMessage()"
              [class]="{
                'self-end': i % 2 !== 0,
                'self-start': i % 2 === 0,
              }"
            />
          }
        } @else {
          @for (
            message of messagesWithLastMessageFromChatbot();
            track $index;
            let first = $first
          ) {
            <squadbox-messages-listing-message
              [id]="'message-' + message.id"
              [isFromUser]="message.isFromUser"
              [chatId]="chatId()"
              [messageId]="message.id"
              [messageContent]="message.content"
              [messageDocuments]="message.documents"
              [isQuickActionsDisplayed]="!message.isFromUser"
              [isLastResponse]="message.isLastMessageFromChatbot"
              [isProcessing]="isThinking()"
              [isNewReply]="!isThinking() && newReplyMessageId() === message.id"
              class="max-w-full"
              [isMobile]="isMobile()"
              [selectedDocumentInChatMessage]="selectedDocumentInChatMessage()"
              [isAssistantDescriptionOrIntroMessage]="
                chatWithAssistant() && first
              "
              [isCompliancePhaseRegular]="isInCompliancePhaseRegular()"
              (documentClicked)="
                handleMessageDocumentClicked({
                  chatMessageId: message.id,
                  documentUuid: $event.documentUuid,
                })
              "
              (imageClicked)="imageInChatMessageClicked.emit($event)"
              (markdownAnimationComplete)="
                handleMarkdownAnimationComplete(message.id)
              "
              (spacerShouldRecalculate)="
                handleSpacerShouldRecalculate(message.id)
              "
            />
          }
        }
      }
    </div>
  `,
})
export class MessagesListingComponent {
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  public readonly chatId = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedChatId,
  );
  public readonly chatWithAssistant = computed(
    () =>
      !!this.chatsStore.selectSignal(chatsFeature.selectSelectedChat)()
        ?.assistantId,
  );
  public readonly isInCompliancePhaseRegular = computed(
    () =>
      this.chatsStore.selectSignal(fromShared.selectComplianceUploadPhase)() ===
      'regular',
  );
  public readonly messages = input.required<MessageListingType[]>();
  public readonly messagesWithLastMessageFromChatbot = computed(() => {
    const messages = this.messages();
    // Filter out the first message if it's not from the user (assistant description)
    const filteredMessages = messages[0]?.isFromUser
      ? messages
      : messages.slice(1);
    const lastMessageFromChatbot = filteredMessages
      .filter((message) => !message.isFromUser)
      .at(-1);

    return messages.map((message) => ({
      ...message,
      documents: excludeInstructionDocuments(message.documents),
      isLastMessageFromChatbot: message.id === lastMessageFromChatbot?.id,
    }));
  });
  public readonly isLoading = input.required<boolean>();
  public readonly isThinking = input.required<boolean>();
  public readonly isMobile = input.required<boolean>();
  public readonly messageCount = input.required<number>();
  public readonly selectedDocumentInChatMessage =
    input<DocumentInChatMessage | null>(null);
  public readonly documentInChatMessageClicked =
    output<SelectDocumentInChatMessagePayload>();
  // Pure passthrough of the bubble's imageClicked (no `chatMessageId`
  // injection, unlike documentInChatMessageClicked above): the image overlay
  // is keyed only by document — it has no per-message citation state.
  public readonly imageInChatMessageClicked =
    output<ImageInMessageClickedEvent>();
  public readonly newReplyAnimationComplete = output<void>();
  public readonly spacerShouldRecalculate = output<void>();
  public readonly loadingMessage = computed(() => {
    const message = 'Lorem ipsum dolor sit amat consectetur.';
    return this.isMobile() ? message : `${message}\n${message}`;
  });

  private prevIsThinking: boolean | null = null;
  public newReplyMessageId = signal<number | null>(null);

  constructor() {
    effect(() => {
      // Reset the "new reply" indicator when navigating to a different chat
      this.chatId();
      this.newReplyMessageId.set(null);
    });

    effect(() => {
      const thinking = this.isThinking();
      const messages = this.messages();
      const filtered = messages[0]?.isFromUser ? messages : messages.slice(1);
      const lastBotMessage = filtered.filter((m) => !m.isFromUser).at(-1);
      const stoppedThinking = !!this.prevIsThinking && !thinking;

      if (thinking) {
        // Clear when the user sends a new message so previous messages don't re-animate
        this.newReplyMessageId.set(null);
      } else if (stoppedThinking && lastBotMessage) {
        // Set the last bot message as a new reply, enables the "new reply" animation
        this.newReplyMessageId.set(lastBotMessage.id);
      }

      this.prevIsThinking = thinking;
    });
  }

  public handleMessageDocumentClicked(
    event: SelectDocumentInChatMessagePayload,
  ) {
    this.documentInChatMessageClicked.emit(event);
  }

  public handleMarkdownAnimationComplete(messageId: number): void {
    if (this.newReplyMessageId() === messageId) {
      this.newReplyAnimationComplete.emit();
    }
  }

  public handleSpacerShouldRecalculate(messageId: number): void {
    if (this.newReplyMessageId() === messageId) {
      this.spacerShouldRecalculate.emit();
    }
  }
}
